import React from "react";
import ReactDOM from "react-dom/client";

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpApi from "i18next-http-backend";
import { z } from "zod";
import { zodErrorMap } from "./utils/zodErrorMap";

import { initAuth, setAuthErrorHandler } from "./services/auth";
import type { AuthErrorReason } from "./services/auth";
import { initSentry } from "./services/sentry";
import en from "./translation/en.json";
import de from "./translation/de.json";
import it from "./translation/it.json";
import fr from "./translation/fr.json";

import App from "./App";
import AuthErrorScreen from "./components/AuthErrorScreen";

i18n
  .use(HttpApi)
  .use(initReactI18next)
  .init({
    supportedLngs: ["en", "de", "it", "fr"],
    resources: {
      en: {
        translation: en,
      },
      de: {
        translation: de,
      },
      it: {
        translation: it,
      },
      fr: {
        translation: fr,
      },
    },
    // Language is driven solely by the user's profile setting (applied in
    // pages/index.js once the user loads, and only when the "localization"
    // feature flag is on); no browser/cookie/localStorage detection. The app
    // boots in English and stays there for non-flagged sessions.
    lng: "en",
    fallbackLng: "en",
    react: { useSuspense: false },
  });

z.config({ customError: zodErrorMap });

initSentry();

const root = ReactDOM.createRoot(document.getElementById("main") as HTMLElement);

// A reload starts a fresh sign-in (onLoad: "login-required"); it only ever happens on the user's click.
const showAuthError = (reason: AuthErrorReason) =>
  root.render(<AuthErrorScreen reason={reason} onRetry={() => window.location.reload()} />);

setAuthErrorHandler(showAuthError);

(async () => {
  try {
    await initAuth();
  } catch (e) {
    // Keycloak unreachable or the sign-in callback failed: an error screen, not a blank page.
    showAuthError("init_failed");
    return;
  }

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
})();
